import type {
  ChunkDefinition,
  ChunkPlacement,
  Lane,
  ObstacleKind,
  PlayerAction,
  SafeRouteStep,
} from './types';

const LENGTH = 36;

function obstacle(offset: number, lane: Lane, kind: ObstacleKind): ChunkPlacement {
  return { offset, lane, kind };
}

function coin(offset: number, lane: Lane): ChunkPlacement {
  return { offset, lane, kind: 'coin' };
}

function route(offset: number, lane: Lane, action: PlayerAction = 'grounded'): SafeRouteStep {
  return { offset, lane, action };
}

function coinLine(lane: Lane, start = 5, count = 5, gap = 2.2): readonly ChunkPlacement[] {
  return Array.from({ length: count }, (_, index) => coin(start + index * gap, lane));
}

export const CHUNK_DEFINITIONS = [
  {
    id: 'welcome-coins',
    difficulty: 'easy',
    length: LENGTH,
    placements: [...coinLine(0, 8, 7, 2.4)],
    safeRoute: [route(18, 0)],
  },
  {
    id: 'first-barrier',
    difficulty: 'easy',
    length: LENGTH,
    placements: [...coinLine(0, 5, 4), obstacle(18, 0, 'barrier'), coin(24, 0), coin(26, 0)],
    safeRoute: [route(18, 0, 'jumping')],
  },
  {
    id: 'first-beam',
    difficulty: 'easy',
    length: LENGTH,
    placements: [...coinLine(0, 5, 4), obstacle(18, 0, 'beam'), coin(24, 0), coin(26, 0)],
    safeRoute: [route(18, 0, 'sliding')],
  },
  {
    id: 'choose-a-lane',
    difficulty: 'easy',
    length: LENGTH,
    placements: [obstacle(17, 0, 'blocker'), ...coinLine(-1, 13, 6, 2.2)],
    safeRoute: [route(17, -1)],
  },
  {
    id: 'split-barriers',
    difficulty: 'medium',
    length: LENGTH,
    placements: [
      obstacle(10, -1, 'blocker'),
      obstacle(10, 1, 'blocker'),
      obstacle(24, 0, 'barrier'),
      coin(18, 0),
      coin(20, 0),
    ],
    safeRoute: [route(10, 0), route(24, 0, 'jumping')],
  },
  {
    id: 'weave-left',
    difficulty: 'medium',
    length: LENGTH,
    placements: [
      obstacle(9, 0, 'blocker'),
      obstacle(9, 1, 'blocker'),
      coin(13, -1),
      coin(15, -1),
      obstacle(24, -1, 'beam'),
    ],
    safeRoute: [route(9, -1), route(24, -1, 'sliding')],
  },
  {
    id: 'weave-right',
    difficulty: 'medium',
    length: LENGTH,
    placements: [
      obstacle(9, -1, 'blocker'),
      obstacle(9, 0, 'blocker'),
      coin(13, 1),
      coin(15, 1),
      obstacle(24, 1, 'barrier'),
    ],
    safeRoute: [route(9, 1), route(24, 1, 'jumping')],
  },
  {
    id: 'action-choice',
    difficulty: 'medium',
    length: LENGTH,
    placements: [
      obstacle(18, -1, 'barrier'),
      obstacle(18, 0, 'beam'),
      obstacle(18, 1, 'blocker'),
      coin(24, -1),
      coin(24, 0),
    ],
    safeRoute: [route(18, -1, 'jumping')],
  },
] as const satisfies readonly ChunkDefinition[];

function matchingObstacle(
  placements: readonly ChunkPlacement[],
  step: SafeRouteStep,
): ObstacleKind | undefined {
  const match = placements.find(
    (placement) =>
      placement.offset === step.offset && placement.lane === step.lane && placement.kind !== 'coin',
  );
  return match?.kind === 'coin' ? undefined : match?.kind;
}

function actionClearsObstacle(
  action: PlayerAction,
  obstacleKind: ObstacleKind | undefined,
): boolean {
  if (obstacleKind === undefined) return true;
  if (obstacleKind === 'barrier') return action === 'jumping';
  if (obstacleKind === 'beam') return action === 'sliding';
  return false;
}

export function validateChunkDefinitions(
  definitions: readonly ChunkDefinition[],
  maxSpeed: number,
  laneChangeDuration: number,
): readonly string[] {
  const errors: string[] = [];
  const ids = new Set<string>();

  for (const definition of definitions) {
    if (ids.has(definition.id)) errors.push(`${definition.id}: duplicate id`);
    ids.add(definition.id);

    for (const placement of definition.placements) {
      if (placement.offset <= 0 || placement.offset >= definition.length) {
        errors.push(`${definition.id}: placement outside chunk at ${placement.offset}`);
      }
    }

    if (definition.safeRoute.length === 0) {
      errors.push(`${definition.id}: missing safe route`);
      continue;
    }

    let previous: SafeRouteStep | undefined;
    for (const step of definition.safeRoute) {
      const hazard = matchingObstacle(definition.placements, step);
      if (!actionClearsObstacle(step.action, hazard)) {
        errors.push(`${definition.id}: route does not clear ${hazard ?? 'row'} at ${step.offset}`);
      }
      if (previous !== undefined) {
        const laneDistance = Math.abs(step.lane - previous.lane);
        const availableTime = (step.offset - previous.offset) / maxSpeed;
        if (availableTime < laneDistance * laneChangeDuration) {
          errors.push(`${definition.id}: route changes lanes too quickly at ${step.offset}`);
        }
      }
      previous = step;
    }
  }

  return errors;
}
